import simplify from '@turf/simplify'
import $ from 'jquery'
import get from 'lodash/get'
import has from 'lodash/has'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'
import set from 'lodash/set'
import unset from 'lodash/unset'
import moment from 'moment'
import OLFormatWFS from 'ol/format/WFS'

import PoziAPIData from 'api/data'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import PoziDrawer from 'drawer/drawer'
import PoziMap from 'map/map'
import PoziNotifications from 'utils/notifications'

export default function PoziGeoServer () {
  // Adapters
  const adapters = {
    /**
     * @api {NA} PoziGeoServer().init Initialise events
     * @apiDescription Add events that trigger load PoziGeoServer.
     * @apiGroup GeoServer
     * @apiName init
     * @apiHeader (Model-On) {Function} Event app.event.drawer.tabs.info.geoserver.edit On event trigger load of GeoServer edit panel.
     */
    init: function () {
      return init()
    },
    /**
     * @api {NA} PoziGeoServer().isGeoServerLayerAndLoggedIn Detect GeoServer Layer
     * @apiDescription Detects if this is a GeoServer layer and user is authorised to edit.
     * @apiGroup GeoServer
     * @apiName isGeoServerLayerAndLoggedIn
     *
     * @apiSuccess {Boolean} true This is a GeoServer layer and user is authorised to edit.
     */
    isGeoServerLayerAndLoggedIn: function (layer) {
      return isGeoServerLayerAndLoggedIn(layer)
    }
  }

  var init = function () {
    $('#pozi-drawer-tabs-panel-info').append($('<div>').attr('id', 'geoserver-info-wrapper'))
    Model.on('app.event.drawer.tabs.info.card.save.button.clicked', _saveButtonClicked)
    Model.on('app.event.drawer.tabs.info.card.delete.button.clicked', _deleteButtonClicked)
    Model.on('app.event.drawer.tabs.info.create.geoserver.click.double', _lineDrawDone)
    Model.on('app.event.drawer.closed', _drawerClosed)
  }

  var isGeoServerLayerAndLoggedIn = function (layerOrTabConfig) {
    // console.log('GeoServer: isGeoServerLayer...')
    Model.unset('app.event.geoserver.loggedin')

    const titleid = get(layerOrTabConfig, 'titleid', null)
    if (isNil(titleid)) {
      // console.log('GeoServer: No titleid found on layer or tabconfig.')
      return false
    }

    const layer = PoziUtils.getLayerFromTitleID(titleid)

    if (isNil(layer)) {
      // console.log('GeoServer: isGeoServerLayer, invalid layer??')
      return false
    }

    const bEditableLayer = has(layer, 'drawer.tabs.info.update.geoserver')
    if (!bEditableLayer) {
      return false
    }
    const policy = get(layer, 'drawer.tabs.info.update.geoserver.auth', '')
    if (policy === 'basic') {
      const bLoggedIn = Model.get('app.event.drawer.tabs.tools.auth.loggedin', false)
      if (!bLoggedIn) {
        console.log('Not logged in.')
        return false
      }
    }
    Model.set('app.event.geoserver.loggedin', true)
    return true
  }

  var _deleteButtonClicked = function () {
    _saveFeature('delete')
  }

  var _saveButtonClicked = function () {
    _saveFeature('update')
  }

  var _lineDrawDone = function () {
    _saveFeature('insert')
  }

  var _saveFeature = function (operation) {
    const options = Model.get('app.event.drawer.tabs.info.card.options', null)
    if (isNil(options)) {
      return _failed('No card details?')
    }
    options.operation = operation
    console.log('GeoServer()._saveFeature', options)
    return _check(options)
      .then(_getTransaction)
      .then(_parseTransaction)
      .then(_sendTransactionToServer)
      .then(_done)
      .then(_success)
      .catch(_failed)
  }

  var _done = function (options) {
    PoziDrawer().hide()
    PoziMap().refreshAllLayers()
  }

  var _check = function (options) {
    return new Promise(function (resolve, reject) {
      let err = ''

      const bLoggedIn = Model.get('app.event.drawer.tabs.tools.auth.loggedin', false)
      if (!bLoggedIn) {
        err = 'Not logged in.'
        return reject(err)
      }

      if (!isObject(options)) {
        err = 'Invalid options.'
        return reject(err)
      }

      if (!has(options, 'layer')) {
        options.layer = Model.get('app.map.target.selection.layer', null)
      }
      if (isNil(options.layer)) {
        err = 'No layer found.'
        return reject(err)
      }
      console.log('layer', options.layer)

      options.geojson = Model.get('app.map.target.featurecollection', null)

      if (!has(options, 'geojson.features[0].properties')) {
        err = 'Invalid GeoJSON! No properties found.'
        return reject(err)
      }

      const props = get(options, 'geojson.features[0].properties', '')
      console.log('GeoJSON properties', props)

      if (has(options, 'geojson.features[0].properties.visited_date')) {
        console.log('Special for Creeklink: Set visited_date to now')
        set(options, 'geojson.features[0].properties.visited_date', moment().format())
      }

      console.log('Remove ID field added by Pozi or the transactions will fail.')
      unset(options, 'geojson.features[0].properties.id')

      options.geojson = simplify(options.geojson, {
        tolerance: 0.00005,
        highQuality: false,
        mutate: true
      })

      options.olFeaturesArr = PoziUtils.convertGeoJSONFeatureCollectionToOLFeatureArray(options.geojson)
      if (options.olFeaturesArr.length === 0 || options.olFeaturesArr.length > 1) {
        err = 'Invalid number of features'
        return reject(err)
      }

      return resolve(options)
    })
  }

  /**
   * @description Gets a GeoServer transaction object from a feature.
   * @name getTransaction
   *
   * @param {Object} layer Configuration object for the layer.
   * @param {Object} olFeature OpenLayers feature.
   */
  var _getTransaction = function (options) {
    return new Promise(function (resolve, reject) {
      let err = ''
      const olFormatWFS = new OLFormatWFS()
      const olFeaturesArr = get(options, 'olFeaturesArr', [])

      if (olFeaturesArr.length !== 1) {
        // Only allow single feature edits.
        err = 'No features found.'
        return reject(err)
      }

      let inserts = []
      let updates = []
      let deletes = []

      const operation = get(options, 'operation', 'update')

      if (operation === 'insert') {
        inserts = get(options, 'olFeaturesArr', [])
      } else if (operation === 'delete') {
        deletes = get(options, 'olFeaturesArr', [])
      } else if (operation === 'update') {
        updates = get(options, 'olFeaturesArr', [])
      }
      options.url = get(options, 'layer.drawer.tabs.info.update.geoserver.url', '')
      if (options.url === '') {
        err = 'No url found.'
        return reject(err)
      }
      const featureType = get(options, 'layer.drawer.tabs.info.update.geoserver.featureType', '')
      if (featureType === '') {
        err = 'No feature type found.'
        return reject(err)
      }
      const writeTransactionOptions = {}
      writeTransactionOptions.featureNS = options.url
      writeTransactionOptions.featurePrefix = ''
      writeTransactionOptions.featureType = featureType

      options.transactionNode = olFormatWFS.writeTransaction(inserts, updates, deletes, writeTransactionOptions)
      if (isNil(options.transactionNode)) {
        err = 'Invalid object returned from writeTransaction.'
        return reject(err)
      }
      return resolve(options)
    })
  }

  var _parseTransaction = function (options) {
    return new Promise(function (resolve, reject) {
      let err = ''

      let transactionXML = options.transactionNode.outerHTML || new window.XMLSerializer().serializeToString(options.transactionNode)
      if (isNil(transactionXML) || transactionXML === '') {
        err = 'Invalid XML returned from transactionNode'
        return reject(err)
      }

      /* INSERT EXAMPLE
        <wfs:Transaction xmlns:wfs="http://www.opengis.net/wfs" service="WFS" version="1.1.0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.1.0/wfs.xsd">
          <wfs:Insert>
            <feature:creek_segment_eol_edit xmlns:feature="http://www.pozi.com/creeklink">
              <feature:the_geom>
                <gml:LineString xmlns:gml="http://www.opengis.net/gml" srsName="EPSG:900913">
                  <gml:posList>16022513.686456 -4645087.0287653 16022599.678113 -4645192.1296792 16022513.686456 -4645244.6801361 16022449.192714 -4645110.9153367</gml:posList>
                </gml:LineString>
              </feature:the_geom>
              <feature:label>test</feature:label>
              <feature:interested>false</feature:interested>
              <feature:visited>false</feature:visited>
              <feature:stock_excluded>false</feature:stock_excluded>
              <feature:native_vegetation>false</feature:native_vegetation>
              <feature:comments>test</feature:comments>
            </feature:creek_segment_eol_edit>
          </wfs:Insert>
        </wfs:Transaction>

        DELETE EXAMPLE

        <wfs:Transaction xmlns:wfs="http://www.opengis.net/wfs" service="WFS" version="1.1.0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.1.0/wfs.xsd">
          <wfs:Delete typeName="feature:creek_segment_eol_edit" xmlns:feature="http://www.pozi.com/creeklink"><ogc:Filter xmlns:ogc="http://www.opengis.net/ogc">
            <ogc:FeatureId fid="creek_segment_eol_edit.14743"/></ogc:Filter>
          </wfs:Delete>
        </wfs:Transaction>
      */

      transactionXML = PoziUtils.replaceAll(transactionXML, '<Transaction', '<wfs:Transaction')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</Transaction>', '</wfs:Transaction>')
      transactionXML = PoziUtils.replaceAll(transactionXML, 'xmlns="http://www.opengis.net/wfs"', 'xmlns:wfs="http://www.opengis.net/wfs"')
      transactionXML = PoziUtils.replaceAll(transactionXML, 'xmlns="http://www.opengis.net/ogc"', 'xmlns:ogc="http://www.opengis.net/ogc"')
      transactionXML = PoziUtils.replaceAll(
        transactionXML,
        'xmlns="http://www.opengis.net/gml"',
        'xmlns:gml="http://www.opengis.net/gml" srsName="EPSG:900913"'
      )
      transactionXML = PoziUtils.replaceAll(transactionXML, '<Update', '<wfs:Update')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</Update>', '</wfs:Update>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<Property', '<wfs:Property')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</Property>', '</wfs:Property>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<Name', '<wfs:Name')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</Name>', '</wfs:Name>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<Value', '<wfs:Value')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</Value>', '</wfs:Value>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<Filter', '<ogc:Filter')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</Filter>', '</ogc:Filter>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<FeatureId', '<ogc:FeatureId')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</FeatureId>', '</ogc:FeatureId>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<LineString', '<gml:LineString')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</LineString>', '</gml:LineString>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<Polygon', '<gml:Polygon')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</Polygon>', '</gml:Polygon>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<posList', '<gml:posList')
      transactionXML = PoziUtils.replaceAll(transactionXML, '</posList>', '</gml:posList>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<wfs:Name>geometry</wfs:Name>', '<wfs:Name>the_geom</wfs:Name>')
      transactionXML = PoziUtils.replaceAll(transactionXML, '<gml:posList srsDimension="2">', '<gml:posList>')
      // Not sure why this URL replace is required... maybe some sort of GeoServer whitelist?? But makes little sense to put a whitelist is in the data.
      transactionXML = PoziUtils.replaceAll(
        transactionXML,
        'https://geoproxy.pozi.com/http://cl-eol.creeklink.lateralplains.com/geoserver/wfs/WfsDispatcher',
        'http://www.pozi.com/creeklink'
      )

      const bInsert = get(options, 'insert', false)
      if (bInsert) {
        transactionXML = PoziUtils.replaceAll(transactionXML, '<Insert', '<wfs:Insert')
        transactionXML = PoziUtils.replaceAll(transactionXML, '</Insert>', '</wfs:Insert>')
        transactionXML = PoziUtils.replaceAll(transactionXML, '<wfs:Insert><', '<wfs:Insert><feature:')
        transactionXML = PoziUtils.replaceAll(transactionXML, '<geometry>', '<feature:the_geom>')
        transactionXML = PoziUtils.replaceAll(transactionXML, '</geometry></', '</feature:the_geom></feature:')
        transactionXML = PoziUtils.replaceAll(transactionXML, ' xmlns=', ' xmlns:feature=')
      }

      const bDelete = get(options, 'delete', false)
      if (bDelete) {
        transactionXML = PoziUtils.replaceAll(transactionXML, '<Delete', '<wfs:Delete')
        transactionXML = PoziUtils.replaceAll(transactionXML, '</Delete>', '</wfs:Delete>')
        transactionXML = PoziUtils.replaceAll(transactionXML, '<wfs:Delete><', '<wfs:Delete><feature:')
        transactionXML = PoziUtils.replaceAll(transactionXML, '<geometry>', '<feature:the_geom>')
        transactionXML = PoziUtils.replaceAll(transactionXML, '</geometry></', '</feature:the_geom></feature:')
        transactionXML = PoziUtils.replaceAll(transactionXML, ' xmlns=', ' xmlns:feature=')
      }

      console.log('GeoServer: transaction - See network tab in Chrome - search WfsDispatcher', transactionXML)

      // if (window.debug.enabled) {
      //   var bAllowedSegments = includes(transactionXML, 'group_boundary_eol_edit.1') || includes(transactionXML, 'creek_segment_eol_edit.4246')
      //   if (!bAllowedSegments) {
      //     console.log('!!!!!!!! TESTING REMOVE IN PROD !!!!!!!!!!!!!.')
      //     console.log('!!!!!!!! TESTING REMOVE IN PROD !!!!!!!!!!!!!.')
      //     console.log('!!!!!!!! TESTING REMOVE IN PROD !!!!!!!!!!!!!.')
      //     console.log('!!!!!!!! TESTING REMOVE IN PROD !!!!!!!!!!!!!.')
      //     err = 'TESTING: Do not attempt to edit a non-test segment!.'
      //     return reject(err)
      //   }
      // }
      options.transactionXML = transactionXML
      return resolve(options)
    })
  }

  var _sendTransactionToServer = function (options) {
    return new Promise(function (resolve, reject) {
      let err = ''
      // console.log('_sendTransactionToServer', options)
      PoziAPIData().ajax({
        method: 'POST',
        dataType: 'xml', // The type of data that you're expecting back from the server
        url: options.url,
        data: options.transactionXML,
        contentType: 'text/plain',
        cache: false,
        headers: {
          'x-pozi-allow-credentials': 'true',
          'x-pozi-single-origin': 'true'
        },
        xhrFields: {
          withCredentials: true
        },
        success: function (data, textStatus, jqXHR) {
          const rawXML = get(jqXHR, 'responseText', '')
          if (!isNil(jqXHR) && includes(rawXML, 'ExceptionReport')) {
            console.error('GeoServer XML error response', rawXML)
            err = 'GeoServer rejected the update.'
            return reject(err)
          } else {
            console.log('GeoServer XML response', rawXML)
          }
          return resolve(options)
        },
        error: reject
      })
    })
  }

  var _drawerClosed = function () {
    console.log('GeoServer: Cleanup.')
    $('#geoserver-info-content').remove()
  }

  var _success = function () {
    console.log('Update complete.')
    PoziNotifications().notify('Feature updated')
    const evt = Model.get('app.event.drawer.tabs.info.create.geoserver.click.event', null)
    if (!isNil(evt)) {
      console.log('Need to wait for layer refresh... difficult to know when this finishes.')
      window.setTimeout(function () {
        console.log('Kick off map click')
        PoziMap().singleClickMap(evt)
        Model.unset('app.event.drawer.tabs.info.create.geoserver.click.event')
      }, 1000)
    }
  }

  var _failed = function (err) {
    console.log('Update failed.', err)
    const msg = 'Failed to update feature! ' + err
    PoziNotifications().warning(msg)
  }
  // Return adapters (must be at end of adapter)
  return adapters
}
